import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, CheckCircle2, Copy, FileUp, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { MANUAL_LABEL_LIMITS, type ManualLabelImportPurpose } from '@threadheads/shared';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { runWithConcurrency } from '@/lib/run-with-concurrency';

const BUCKET = 'portless-manual-labels';
const ACTIVE = new Set(['sealed', 'processing', 'applying']);
const APPLY_POLICY_CONFLICTS = new Set([
  'label_set_conflict',
  'label_set_locked',
  'label_set_exists',
  'tracking_conflict',
  'lifecycle_conflict',
  'apply_failed',
]);
const STALLED_AFTER_MS = 60_000;
// Lifecycle blocks are operator instructions, not debug metadata — rendered
// at readable size, not muted.
const LIFECYCLE_REASONS = new Set(['already_manifested', 'not_shipped', 'order_terminal']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface ManualLabelImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string | null;
  purpose: ManualLabelImportPurpose;
  targetProdOrderId?: string;
  onApplied?: () => void;
}

export function ManualLabelImportDialog({
  open,
  onOpenChange,
  locationId,
  purpose,
  targetProdOrderId,
  onApplied,
}: ManualLabelImportDialogProps) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const [files, setFiles] = useState<File[]>([]);
  const [importId, setImportId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [isStalled, setIsStalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [resultPage, setResultPage] = useState(1);
  const [copyingSafeList, setCopyingSafeList] = useState(false);
  const [confirmingDecision, setConfirmingDecision] = useState<Record<string, { decision: 'replace' | 'keep'; armedAt: number } | undefined>>({});
  const [rowNotices, setRowNotices] = useState<Record<string, string | undefined>>({});
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const [resolvedConflicts, setResolvedConflicts] = useState<Array<{
    pageResultId: string; orderName: string; tracking: string; decision: 'replace' | 'keep';
  }>>([]);
  const notifiedAppliedCountRef = useRef(0);
  const serverProgressKeyRef = useRef<string | null>(null);
  const lastServerProgressAtRef = useRef(Date.now());
  const createImport = trpc.manualLabels.createImport.useMutation();
  const mintTargets = trpc.manualLabels.mintUploadTargets.useMutation();
  const seal = trpc.manualLabels.finalizeAndSeal.useMutation();
  const cancel = trpc.manualLabels.cancelImport.useMutation();
  const retry = trpc.manualLabels.retryDocuments.useMutation();
  const mapPages = trpc.manualLabels.mapPages.useMutation();
  const resolveConflict = trpc.manualLabels.resolveTrackingConflict.useMutation();
  const status = trpc.manualLabels.getImportStatus.useQuery(
    { importId: importId ?? '00000000-0000-0000-0000-000000000000' },
    {
      enabled: Boolean(importId),
      refetchInterval: (query) => ACTIVE.has(query.state.data?.status ?? '') ? 2_000 : false,
    },
  );
  const results = trpc.manualLabels.listImportResults.useQuery(
    { importId: importId ?? '00000000-0000-0000-0000-000000000000', page: resultPage, pageSize: 100, reviewOnly: true },
    { enabled: Boolean(importId) && ['review', 'partial', 'complete'].includes(status.data?.status ?? '') },
  );
  const current = status.data;
  const serverBusy = ACTIVE.has(current?.status ?? '');
  const serverProgressKey = current
    ? [
        current.status,
        current.counters.pages,
        current.counters.parsedDocuments,
        current.counters.failedDocuments,
        current.counters.exactPages,
        current.counters.unresolvedPages,
        current.counters.appliedOrders,
      ].join(':')
    : null;

  const reviewRows = useMemo(
    () => (results.data?.results ?? []).filter((row) => ['unmapped', 'invalid', 'conflict'].includes(row.status)),
    [results.data],
  );
  const resumeKey = locationId
    ? `manual-label-import:${locationId}:${purpose}:${targetProdOrderId ?? 'bulk'}`
    : null;

  useEffect(() => {
    if (!open || importId || !resumeKey) return;
    const saved = window.localStorage.getItem(resumeKey);
    if (saved) setImportId(saved);
  }, [open, importId, resumeKey]);

  useEffect(() => {
    if (importId && resumeKey) window.localStorage.setItem(resumeKey, importId);
  }, [importId, resumeKey]);

  useEffect(() => {
    if (!importId || !serverBusy || !serverProgressKey) {
      serverProgressKeyRef.current = null;
      setIsStalled(false);
      return;
    }

    if (serverProgressKeyRef.current !== serverProgressKey) {
      serverProgressKeyRef.current = serverProgressKey;
      lastServerProgressAtRef.current = Date.now();
      setIsStalled(false);
    }

    const elapsed = Date.now() - lastServerProgressAtRef.current;
    if (elapsed >= STALLED_AFTER_MS) {
      setIsStalled(true);
      return;
    }
    const timer = window.setTimeout(() => setIsStalled(true), STALLED_AFTER_MS - elapsed);
    return () => window.clearTimeout(timer);
  }, [importId, serverBusy, serverProgressKey]);

  useEffect(() => {
    const applied = status.data?.counters.appliedOrders ?? 0;
    if (applied <= notifiedAppliedCountRef.current) return;
    notifiedAppliedCountRef.current = applied;
    void utils.shipping.listPortlessBlocked.invalidate();
    void utils.productionOrders.getOrderQueue.invalidate();
    onApplied?.();
  }, [status.data?.counters.appliedOrders, onApplied, utils]);

  useEffect(() => {
    const total = results.data?.pagination.total ?? 0;
    const lastPage = Math.max(1, Math.ceil(total / 100));
    if (resultPage > lastPage) setResultPage(lastPage);
  }, [resultPage, results.data?.pagination.total]);

  // An armed confirm must not survive the row list changing under it.
  useEffect(() => {
    setConfirmingDecision({});
  }, [resultPage]);

  const reset = () => {
    setFiles([]);
    setImportId(null);
    setProgress(0);
    setUploading(false);
    setIsStalled(false);
    setError(null);
    setMapping({});
    setResultPage(1);
    setConfirmingDecision({});
    setRowNotices({});
    setCopiedRowId(null);
    setResolvedConflicts([]);
    notifiedAppliedCountRef.current = 0;
    serverProgressKeyRef.current = null;
  };

  // Arming only swaps the label; the commit click is ignored for 450ms so a
  // double-click cannot fire the destructive action, and an armed button
  // disarms itself after 6s so a stale arm never fires on a stray tap.
  const armDecision = (rowId: string, decision: 'replace' | 'keep') => {
    const armedAt = Date.now();
    setConfirmingDecision((old) => ({ ...old, [rowId]: { decision, armedAt } }));
    window.setTimeout(() => {
      setConfirmingDecision((old) => old[rowId]?.armedAt === armedAt ? { ...old, [rowId]: undefined } : old);
    }, 6_000);
  };

  const resolveTrackingConflictRow = async (
    row: { id: string; orderNumber: string | null },
    decision: 'replace' | 'keep',
  ) => {
    if (!importId) return;
    const armed = confirmingDecision[row.id];
    if (!armed || armed.decision !== decision || Date.now() - armed.armedAt < 450) return;
    setError(null);
    setRowNotices((old) => ({ ...old, [row.id]: undefined }));
    try {
      const result = await resolveConflict.mutateAsync({ importId, pageResultId: row.id, decision });
      const appliedTracking = result.applied ? result.tracking : null;
      if (appliedTracking) {
        setResolvedConflicts((old) => [...old, {
          pageResultId: row.id,
          orderName: result.orderName ?? row.orderNumber ?? '',
          tracking: appliedTracking,
          decision,
        }]);
      } else {
        setRowNotices((old) => ({ ...old, [row.id]: t('manualLabels.trackingConflict.stale') }));
      }
      await Promise.all([status.refetch(), results.refetch()]);
    } catch (resolveError) {
      setRowNotices((old) => ({ ...old, [row.id]: getUserMessage(resolveError) }));
    } finally {
      setConfirmingDecision((old) => ({ ...old, [row.id]: undefined }));
    }
  };

  // Known reason codes render in the operator's language; unknown codes fall
  // back to the raw slug so nothing is ever hidden.
  const reasonLabel = (code: string, orderNumber?: string | null) => {
    if (code === 'already_manifested' && orderNumber) {
      return t('manualLabels.reason.already_manifested_order', { order: orderNumber });
    }
    // Inline template literal: the i18n parity checker derives the dynamic
    // key family from the t() call site itself.
    const translated = t(`manualLabels.reason.${code}`);
    return translated === `manualLabels.reason.${code}` ? code : translated;
  };

  const copyClientMessage = async (row: { id: string; orderNumber: string | null; trackingNumber: string | null; reasonDetail: { orderName?: string | null; existingTracking?: string | null } | null }) => {
    try {
      await navigator.clipboard.writeText(t('manualLabels.trackingConflict.clientMessage', {
        order: row.reasonDetail?.orderName ?? row.orderNumber ?? '',
        existing: row.reasonDetail?.existingTracking ?? '',
        incoming: row.trackingNumber ?? '',
      }));
      setCopiedRowId(row.id);
      window.setTimeout(() => setCopiedRowId((old) => (old === row.id ? null : old)), 1_500);
    } catch (copyError) {
      setRowNotices((old) => ({ ...old, [row.id]: getUserMessage(copyError) }));
    }
  };

  const copySafeList = async () => {
    if (!importId) return;
    setCopyingSafeList(true);
    setError(null);
    try {
      const total = results.data?.pagination.total ?? 0;
      const pageCount = Math.max(1, Math.ceil(total / 100));
      const pages = await Promise.all(Array.from({ length: pageCount }, (_, index) =>
        utils.manualLabels.listImportResults.fetch({
          importId, page: index + 1, pageSize: 100, reviewOnly: true,
        })));
      await navigator.clipboard.writeText(pages.flatMap((page) => page.results)
        .map((row) => row.slackSafeText).join('\n'));
    } catch (copyError) {
      setError(getUserMessage(copyError));
    } finally {
      setCopyingSafeList(false);
    }
  };

  const handleOpen = (next: boolean) => {
    if (!next && (uploading || createImport.isPending || mintTargets.isPending || seal.isPending
      || cancel.isPending || retry.isPending || mapPages.isPending || resolveConflict.isPending)) return;
    if (!next) {
      // Server-side parsing/applying is durable, so closing is safe and the
      // local pointer resumes it next time. Only browser-side upload/mutations
      // block close. Completed/failed/cancelled work no longer needs the pointer.
      if (resumeKey && (!importId || ['complete', 'failed', 'cancelled'].includes(status.data?.status ?? ''))) {
        window.localStorage.removeItem(resumeKey);
      }
      reset();
    }
    onOpenChange(next);
  };

  const chooseFiles = (selected: FileList | null) => {
    const next = [...(selected ?? [])];
    setError(null);
    if (next.length > MANUAL_LABEL_LIMITS.maxFiles) {
      setError(t('manualLabels.validation.maxFiles'));
      return;
    }
    if (next.some((file) => file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf'))) {
      setError(t('manualLabels.validation.pdfOnly'));
      return;
    }
    if (next.some((file) => file.size > MANUAL_LABEL_LIMITS.maxFileBytes)) {
      setError(t('manualLabels.validation.fileSize'));
      return;
    }
    if (next.reduce((sum, file) => sum + file.size, 0) > MANUAL_LABEL_LIMITS.maxBatchBytes) {
      setError(t('manualLabels.validation.batchSize'));
      return;
    }
    setFiles(next);
  };

  const upload = async () => {
    if (!locationId || files.length === 0) return;
    setError(null);
    setProgress(2);
    setUploading(true);
    try {
      const metadata = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index]!;
        metadata.push({ filename: file.name, sizeBytes: file.size, sha256: await sha256(file) });
        setProgress(Math.round(((index + 1) / files.length) * 15));
      }
      const created = await createImport.mutateAsync({
        locationId,
        purpose,
        targetProdOrderId,
        files: metadata,
      });
      setImportId(created.importId);
      const targetByDocument = new Map<string, { path: string; token: string }>();
      for (let index = 0; index < created.documents.length; index += MANUAL_LABEL_LIMITS.signedTargetsPerRequest) {
        const chunk = created.documents.slice(index, index + MANUAL_LABEL_LIMITS.signedTargetsPerRequest);
        const targets = await mintTargets.mutateAsync({ importId: created.importId, documentIds: chunk.map((doc) => doc.id) });
        targets.forEach((target) => targetByDocument.set(target.documentId, target));
      }
      let uploaded = 0;
      await runWithConcurrency(created.documents.map((document) => async () => {
        const file = files[document.sequence - 1]!;
        const target = targetByDocument.get(document.id)!;
        const result = await supabase.storage.from(BUCKET).uploadToSignedUrl(target.path, target.token, file, {
          contentType: 'application/pdf', upsert: false,
        });
        if (result.error) throw result.error;
        uploaded += 1;
        setProgress(15 + Math.round((uploaded / files.length) * 70));
      }), MANUAL_LABEL_LIMITS.browserUploadConcurrency);
      await seal.mutateAsync({ importId: created.importId });
      setProgress(100);
      await status.refetch();
    } catch (uploadError) {
      setError(getUserMessage(uploadError));
    } finally {
      setUploading(false);
    }
  };

  const busy = uploading || createImport.isPending || mintTargets.isPending || seal.isPending || serverBusy;
  const fileBytes = files.reduce((sum, file) => sum + file.size, 0);
  const hasPendingMappings = Object.values(mapping).some((value) => value.trim());
  const canMap = (row: (typeof reviewRows)[number]) => Boolean(
    row.trackingNumber
    && ['unmapped', 'conflict'].includes(row.status)
    && !APPLY_POLICY_CONFLICTS.has(row.reasonCode ?? '')
    && !['duplicate_page', 'duplicate_tracking', 'outside_target_order'].includes(row.reasonCode ?? ''),
  );
  const canCancelCurrent = ['draft', 'sealed', 'processing', 'review'].includes(current?.status ?? '')
    && (current?.counters.appliedOrders ?? 0) === 0;
  const serverProgress = current?.status === 'sealed'
    ? 10
    : current?.status === 'processing'
      ? 15 + Math.round(
          ((current.counters.parsedDocuments + current.counters.failedDocuments)
            / Math.max(current.counters.files, 1)) * 65,
        )
      : current?.status === 'applying'
        ? 85
        : 100;
  const displayedProgress = uploading ? Math.max(progress, 2) : serverBusy ? serverProgress : 100;

  const refreshCurrentStatus = async () => {
    try {
      setError(null);
      await status.refetch();
    } catch (refreshError) {
      setError(getUserMessage(refreshError));
    }
  };

  const cancelCurrentImport = async () => {
    if (!importId) return;
    try {
      setError(null);
      await cancel.mutateAsync({ importId });
      await status.refetch();
    } catch (cancelError) {
      setError(getUserMessage(cancelError));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{purpose === 'manifest_recovery' ? t('manualLabels.titleManifest') : t('manualLabels.titleProduction')}</DialogTitle>
          <DialogDescription>
            {t('manualLabels.description')}
          </DialogDescription>
        </DialogHeader>

        {!importId ? (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="manual-label-files">{t('manualLabels.files')}</Label>
              <Input id="manual-label-files" type="file" accept="application/pdf,.pdf" multiple onChange={(event) => chooseFiles(event.target.files)} />
              <p className="text-xs text-muted-foreground">{t('manualLabels.fileSummary', { count: files.length, mb: (fileBytes / 1024 / 1024).toFixed(1) })}</p>
            </div>
            <Button onClick={() => void upload()} disabled={!locationId || files.length === 0 || busy} className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              {t('manualLabels.upload')}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{t('manualLabels.statusLabel', { status: t(`manualLabels.status.${current?.status ?? 'uploading'}`) })}</span>
                <span>{t('manualLabels.pageCount', { count: current?.counters.pages ?? 0 })}</span>
              </div>
              <Progress
                value={displayedProgress}
                aria-label={t('manualLabels.progressLabel')}
                className={serverBusy ? '[&>div]:animate-pulse' : undefined}
              />
              {current && <p className="text-xs text-muted-foreground">{t('manualLabels.counterSummary', { applied: current.counters.appliedOrders, review: current.counters.unresolvedPages, failed: current.counters.failedDocuments })}</p>}
              {serverBusy && <p className="text-xs text-muted-foreground">{t('manualLabels.processingContinues')}</p>}
            </div>

            {isStalled && serverBusy && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t('manualLabels.stalledTitle')}</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>{t('manualLabels.stalledDescription')}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" disabled={status.isFetching} onClick={() => void refreshCurrentStatus()}>
                      {status.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                      {t('manualLabels.refreshStatus')}
                    </Button>
                    {canCancelCurrent && (
                      <Button variant="destructive" size="sm" disabled={cancel.isPending} onClick={() => void cancelCurrentImport()}>
                        {t('manualLabels.cancel')}
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {status.isError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t('manualLabels.resumeFailedTitle')}</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>{getUserMessage(status.error)}</p>
                  <Button variant="outline" size="sm" onClick={() => {
                    if (resumeKey) window.localStorage.removeItem(resumeKey);
                    reset();
                  }}>{t('manualLabels.startOver')}</Button>
                </AlertDescription>
              </Alert>
            )}

            {current?.status === 'complete' && (
              <Alert><CheckCircle2 className="h-4 w-4" /><AlertTitle>{t('manualLabels.completeTitle')}</AlertTitle><AlertDescription>{t('manualLabels.completeDescription')}</AlertDescription></Alert>
            )}
            {(current?.status === 'review' || current?.status === 'partial') && (
              <Alert variant="warning"><AlertTriangle className="h-4 w-4" /><AlertTitle>{t('manualLabels.reviewTitle')}</AlertTitle><AlertDescription>{t('manualLabels.reviewDescription')}</AlertDescription></Alert>
            )}
            {current?.status === 'failed' && (
              <Alert variant="destructive"><XCircle className="h-4 w-4" /><AlertTitle>{t('manualLabels.failedTitle')}</AlertTitle><AlertDescription>{current.errorMessage}</AlertDescription></Alert>
            )}

            {reviewRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">{t('manualLabels.reviewQueue')}</p>
                  <Button variant="outline" size="sm" disabled={copyingSafeList} onClick={() => void copySafeList()}>
                    {copyingSafeList ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
                    {t('manualLabels.copySafeList')}
                  </Button>
                </div>
                {reviewRows.map((row) => (
                  <div key={row.id} className="space-y-2 rounded-md border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium">{t('manualLabels.filePage', { filename: row.filename, page: row.sourcePage })}</p>
                      <Button variant="ghost" size="sm" onClick={() => void navigator.clipboard.writeText(row.slackSafeText)}><Copy className="h-4 w-4" /><span className="sr-only">{t('manualLabels.copySafeDetails')}</span></Button>
                    </div>
                    {!(row.reasonCode === 'tracking_conflict' && row.reasonDetail?.existingTracking && row.trackingNumber) && (
                      <p className={LIFECYCLE_REASONS.has(row.reasonCode ?? '') ? 'text-sm' : 'text-xs text-muted-foreground'}>{t('manualLabels.trackingReason', { tracking: row.trackingNumber ?? t('manualLabels.missing'), reason: reasonLabel(row.reasonCode ?? row.status, row.orderNumber) })}</p>
                    )}
                    {row.reasonCode === 'tracking_conflict' && row.reasonDetail?.existingTracking && row.trackingNumber ? (
                      <div className="space-y-3">
                        <p className="text-base">
                          {t('manualLabels.trackingConflict.explain', {
                            order: row.reasonDetail.orderName ?? row.orderNumber ?? '',
                          })}
                        </p>
                        <div className="space-y-1 font-mono text-sm tabular-nums">
                          <p>{t('manualLabels.trackingConflict.existingLabel')}: {row.reasonDetail.existingTracking}</p>
                          <p>{t('manualLabels.trackingConflict.uploadedLabel')}: {row.trackingNumber}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            className="h-12 min-w-48"
                            variant={confirmingDecision[row.id]?.decision === 'replace' ? 'destructive' : 'outline'}
                            disabled={resolveConflict.isPending}
                            onClick={() => confirmingDecision[row.id]?.decision === 'replace'
                              ? void resolveTrackingConflictRow(row, 'replace')
                              : armDecision(row.id, 'replace')}
                          >
                            {resolveConflict.isPending && confirmingDecision[row.id]?.decision === 'replace' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            {confirmingDecision[row.id]?.decision === 'replace'
                              ? t('manualLabels.trackingConflict.replaceConfirm', { incoming: row.trackingNumber })
                              : t('manualLabels.trackingConflict.replace')}
                          </Button>
                          <Button
                            className="h-12 min-w-48"
                            variant="outline"
                            disabled={resolveConflict.isPending}
                            onClick={() => confirmingDecision[row.id]?.decision === 'keep'
                              ? void resolveTrackingConflictRow(row, 'keep')
                              : armDecision(row.id, 'keep')}
                          >
                            {resolveConflict.isPending && confirmingDecision[row.id]?.decision === 'keep' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            {confirmingDecision[row.id]?.decision === 'keep'
                              ? t('manualLabels.trackingConflict.keepConfirm', { existing: row.reasonDetail.existingTracking })
                              : t('manualLabels.trackingConflict.keep')}
                          </Button>
                          <Button
                            className="h-12"
                            variant="ghost"
                            onClick={() => void copyClientMessage(row)}
                          >
                            {copiedRowId === row.id ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                            {copiedRowId === row.id ? t('manualLabels.trackingConflict.copied') : t('manualLabels.trackingConflict.copyMessage')}
                          </Button>
                        </div>
                        {rowNotices[row.id] && (
                          <p className="text-sm text-destructive">{rowNotices[row.id]}</p>
                        )}
                      </div>
                    ) : canMap(row) ? (
                      <Input value={mapping[row.id] ?? ''} onChange={(event) => setMapping((old) => ({ ...old, [row.id]: event.target.value }))} placeholder={t('manualLabels.orderPlaceholder')} />
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {APPLY_POLICY_CONFLICTS.has(row.reasonCode ?? '')
                          ? t('manualLabels.applyConflict')
                          : t('manualLabels.cannotMap')}
                      </p>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={resultPage === 1 || hasPendingMappings} onClick={() => setResultPage((page) => page - 1)}>{t('manualLabels.previous')}</Button>
                    <span className="text-xs text-muted-foreground">{t('manualLabels.pagination', { page: resultPage, total: Math.max(1, Math.ceil((results.data?.pagination.total ?? 0) / 100)) })}</span>
                    <Button variant="outline" size="sm" disabled={hasPendingMappings || resultPage * 100 >= (results.data?.pagination.total ?? 0)} onClick={() => setResultPage((page) => page + 1)}>{t('manualLabels.next')}</Button>
                  </div>
                  {reviewRows.some(canMap) && (
                    <Button disabled={mapPages.isPending || Object.values(mapping).every((value) => !value.trim())} onClick={async () => {
                      const mappings = Object.entries(mapping).filter(([, value]) => value.trim()).map(([pageResultId, value]) => {
                        const identifier = value.trim();
                        return UUID.test(identifier)
                          ? { pageResultId, prodOrderId: identifier }
                          : { pageResultId, orderNumber: identifier };
                      });
                      try {
                        setError(null);
                        await mapPages.mutateAsync({ importId, mappings });
                        setMapping({});
                        await Promise.all([status.refetch(), results.refetch()]);
                      } catch (mappingError) {
                        setError(getUserMessage(mappingError));
                      }
                    }}>{mapPages.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t('manualLabels.applyMappings')}</Button>
                  )}
                </div>
              </div>
            )}

            {resolvedConflicts.length > 0 && (
              <Alert className="border-emerald-600/40 text-emerald-700 dark:text-emerald-400 [&>svg]:text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>{t('manualLabels.trackingConflict.resolvedTitle')}</AlertTitle>
                <AlertDescription>
                  <ul className="space-y-1">
                    {resolvedConflicts.map((entry) => (
                      <li key={entry.pageResultId}>
                        {t(entry.decision === 'replace'
                          ? 'manualLabels.trackingConflict.resolvedReplaced'
                          : 'manualLabels.trackingConflict.resolvedKept', {
                          order: entry.orderName,
                          tracking: entry.tracking,
                        })}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              {(current?.failedDocumentIds.length ?? 0) > 0 && (
                <Button variant="outline" disabled={retry.isPending} onClick={async () => {
                  try {
                    setError(null);
                    await retry.mutateAsync({ importId, documentIds: current!.failedDocumentIds });
                    await status.refetch();
                  } catch (retryError) {
                    setError(getUserMessage(retryError));
                  }
                }}><RefreshCw className="mr-2 h-4 w-4" />{t('manualLabels.retry')}</Button>
              )}
              {canCancelCurrent && !isStalled && (
                <Button variant="destructive" disabled={cancel.isPending} onClick={() => void cancelCurrentImport()}>
                  {t('manualLabels.cancel')}
                </Button>
              )}
              {!busy && <Button onClick={() => handleOpen(false)}>{t('manualLabels.done')}</Button>}
            </div>
          </div>
        )}

        {error && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>{t('manualLabels.errorTitle')}</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      </DialogContent>
    </Dialog>
  );
}
